/* eslint-disable max-lines */
import { createTheme } from '@material-ui/core/styles';
import { alpha, darken, lighten } from '@material-ui/core/styles/colorManipulator';
import createPalette from '@material-ui/core/styles/createPalette';
import { Inter, InterFontNames } from './fonts';
export const c2foThemeFallbackFonts = [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    '"Helvetica"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
];
const InterFontStyles = {
    fontFamily: [InterFontNames.normal, ...c2foThemeFallbackFonts].join(', '),
    '@supports (font-variation-settings: normal)': {
        fontFamily: [InterFontNames.var, ...c2foThemeFallbackFonts].join(', '),
    },
};
export const palette = createPalette({
    type: 'light',
    text: {
        secondary: 'rgba(0, 0, 0, 0.54)',
        disabled: 'rgba(0, 0, 0, 0.38)',
        inverse: '#FFFFFF',
    },
    primary: {
        main: '#007F6E',
        dark: '#095540',
        light: '#6BA43A',
    },
    secondary: {
        main: '#005D83',
        light: '#04a1C8',
    },
    error: {
        main: '#BB3100',
        lightShade190p: '#f8eae5',
    },
    warning: {
        main: '#F5A623',
        lightShade190p: '#fef6e9',
    },
    info: {
        main: '#D5D0CC',
        light: '#F6F5F4',
    },
    success: {
        main: '#04a1C8',
        lightShade190p: '#E5F5F9',
    },
    background: {
        canvas: '#f6f6f6',
        hint: '#333',
        backdropOverlay: 'rgba(172, 161, 153, 0.7)',
    },
    common: {
        black: '#000000',
        white: '#FFFFFF',
    },
    grey: {
        500: '#949494',
    },
    deepGrey: {
        100: 'rgba(155, 155, 155, 0.45)',
        200: 'rgba(57, 57, 57, 0.42)',
        300: '#979797',
    },
    green: {},
    blue: {},
    deepBlue: {},
    red: {},
    pink: {},
    purple: {},
    deepPurple: {},
    lightBlue: {},
    cyan: {},
    teal: {},
    lightGreen: {},
    lime: {},
    yellow: {},
    amber: {},
    orange: {},
    deepOrange: {},
    blueGrey: {},
    liquidity: {
        textStandard: 'rgba(0, 0, 0, 0.7)',
        supportingGrey: '#aca199',
        miscCanvas: '#f5f3f2',
        grey10: '#F6F5F4',
        grey25: '#eae8e6',
        brightBlue: '#0095a9',
    },
});
/**
 * C2FO Theme
 * This is the base theme for C2FO (legacy). It defines the color scheme and typography that material-ui will use
 */
export const C2FOTheme = createTheme({
    name: 'c2fo',
    overrides: {
        PaginationItem: {
            root: {
                // fontSize: pxToRem(16),
                fontFamily: '"Inter var", sans-serif',
                color: palette.liquidity.textStandard,
                '&:hover, &$focusVisible': {
                    backgroundColor: lighten(palette.primary.dark, 0.95),
                },
                '&$selected': {
                    backgroundColor: lighten(palette.primary.dark, 0.85),
                    color: palette.liquidity.textStandard,
                    fontWeight: 700,
                    '&:hover, &$focusVisible': {
                        backgroundColor: lighten(palette.primary.dark, 0.75),
                    },
                },
                '& .MuiPaginationItem-icon': {
                    color: palette.liquidity.textStandard,
                },
            },
            selected: {},
            focusVisible: {},
        },
        MuiCssBaseline: {
            '@global': {
                '@font-face': Inter,
                'input[type="search"]::-webkit-search-cancel-button': {
                    '-webkit-appearance': 'none',
                },
                html: Object.assign({ '-webkit-text-size-adjust': '100%' }, InterFontStyles),
                body: {
                    backgroundColor: palette.liquidity.miscCanvas,
                },
            },
        },
        MuiTypography: {
            root: Object.assign({ color: palette.liquidity.textStandard }, InterFontStyles),
        },
        MuiOutlinedInput: {
            root: {
                '&.Mui-focused:not(.Mui-error) $notchedOutline': {
                    borderColor: palette.liquidity.brightBlue,
                },
            },
        },
        MuiButton: {
            root: {
                border: '1px solid transparent',
                fontSize: '1rem',
                fontWeight: 600,
            },
            text: {
                // border set zero to handle ButtonGroup implementation where button borders visible for text variants
                border: 0,
            },
            disabled: {},
            outlined: {
                borderWidth: '2px',
                '&$disabled, &:hover': {
                    borderWidth: '2px',
                },
            },
            outlinedSecondary: {
                borderWidth: '2px',
                borderColor: palette.secondary.main,
                '&:hover': {
                    borderWidth: '2px',
                },
            },
            containedSecondary: {},
            sizeLarge: {
                fontSize: '1.1875rem',
                fontWeight: 700,
            },
        },
        CTAButton: {
            root: {
                backgroundColor: palette.primary.light,
                borderColor: palette.primary.light,
                color: palette.common.white,
                '&:hover': {
                    // TODO check if this is correct
                    backgroundColor: darken(palette.primary.light, 0.15),
                    borderColor: darken(palette.primary.light, 0.2),
                },
            },
            containedSecondary: {
                borderColor: lighten(palette.primary.light, 0.05),
                '&:hover': {
                    borderColor: darken(palette.primary.light, 0.1),
                },
                '&$disabled': {
                    backgroundColor: palette.liquidity.grey25,
                    borderColor: palette.info.main,
                },
            },
            disabled: {
                borderColor: palette.liquidity.grey25,
            },
        },
        MuiTable: {
            root: {
                borderCollapse: 'collapse',
                borderSpacing: 0,
            },
        },
        MuiTooltip: {
            tooltip: {
                fontSize: '0.875rem',
                fontWeight: 500,
                padding: '16px 16px',
                borderRadius: 3,
            },
        },
        MuiTableRow: {
            root: {
                'tbody > &': {
                    '&$selected': {
                        backgroundColor: 'inherit',
                    },
                    '&:nth-of-type(even)': {
                        backgroundColor: palette.background.canvas,
                    },
                    // leave the hover state after the nth-of-type for specificity
                    '&$hover:hover, &$selected:hover': {
                        backgroundColor: alpha(palette.success.main, 0.1),
                    },
                },
            },
        },
        MuiTableCell: {
            root: {
                borderBottom: 'none',
                padding: 8,
                '&:first-child': {
                    paddingLeft: 16,
                },
                '&:last-child': {
                    paddingRight: 16,
                },
                'thead tr:last-child &': {
                    borderBottom: `1px solid #003a50`,
                },
            },
        },
        MuiCheckbox: {
            root: {
                color: palette.deepGrey[300],
                '&$disabled': {
                    color: palette.deepGrey[100],
                },
                '&$checked': {
                    color: palette.success.main,
                    '&$disabled': {
                        color: palette.deepGrey[100],
                    },
                },
            },
            colorPrimary: {
                '&$disabled': {
                    color: palette.deepGrey[100],
                },
                '&$checked': {
                    color: palette.primary.light,
                    '&$disabled': {
                        color: palette.deepGrey[100],
                    },
                },
            },
            colorSecondary: {
                '&$disabled': {
                    color: palette.deepGrey[100],
                },
                '&$checked': {
                    color: palette.secondary.main,
                    '&$disabled': {
                        color: palette.deepGrey[100],
                    },
                },
            },
        },
        MuiChip: {
            root: {
                color: palette.liquidity.textStandard,
                backgroundColor: palette.info.main,
                borderRadius: '13.5px',
                height: 'auto',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                margin: '2px',
            },
            deleteIcon: {
                height: '16px',
            },
        },
        MuiSwitch: {
            track: {
                backgroundColor: palette.grey[500],
            },
            colorSecondary: {
                color: palette.common.white,
                '&$checked': {
                    color: palette.secondary.light,
                    '&:hover': {
                        backgroundColor: alpha(palette.secondary.light, palette.action.hoverOpacity),
                        '@media (hover: none)': {
                            backgroundColor: 'transparent',
                        },
                    },
                },
                '&$disabled': {
                    color: palette.type === 'light' ? palette.grey[400] : palette.grey[800],
                },
                '&$checked + $track': {
                    backgroundColor: palette.secondary.light,
                },
                '&$disabled + $track': {
                    backgroundColor: palette.type === 'light' ? palette.common.black : palette.common.white,
                },
            },
        },
        MuiRadio: {
            root: {
                color: palette.deepGrey[300],
                '&$disabled': {
                    color: palette.deepGrey[100],
                },
                '&$checked': {
                    color: palette.success.main,
                    '&$disabled': {
                        color: palette.deepGrey[100],
                    },
                },
            },
            colorPrimary: {
                '&$disabled': {
                    color: palette.deepGrey[100],
                },
                '&$checked': {
                    color: palette.primary.light,
                    '&$disabled': {
                        color: palette.deepGrey[100],
                    },
                },
            },
            colorSecondary: {
                '&$disabled': {
                    color: palette.deepGrey[100],
                },
                '&$checked': {
                    color: palette.secondary.main,
                    '&$disabled': {
                        color: palette.deepGrey[100],
                    },
                },
            },
        },
        AlertBanner: {
            filledInfo: {
                backgroundColor: lighten(palette.background.backdropOverlay, 0.5),
                color: palette.liquidity.textStandard,
            },
            filledWarning: {
                color: palette.liquidity.textStandard,
            },
            filledSuccess: {
                color: palette.liquidity.textStandard,
            },
            standardSuccess: {
                borderColor: darken(palette.success.main, 0.6),
            },
            standardInfo: {
                borderColor: darken(palette.info.main, 0.6),
            },
            standardWarning: {
                borderColor: darken(palette.warning.main, 0.6),
            },
            standardError: {
                borderColor: darken(palette.warning.main, 0.6),
            },
        },
        MuiAvatar: {
            root: {
                width: '16px',
                height: '16px',
                fontSize: 'x-small',
                fontWeight: 'bold',
            },
        },
    },
    props: {
        MuiButton: {
            disableElevation: true,
        },
        MuiLink: {
            color: 'secondary',
        },
        MuiSwitch: {
            color: 'secondary',
        },
        MuiCheckbox: {
            color: 'default',
        },
        MuiRadio: {
            color: 'default',
        },
    },
    palette,
    shape: {
        borderRadius: 5,
    },
    spacing: 8,
    typography: {
        allVariants: {
            fontFamily: undefined, // set to undefined to avoid default material font family
        },
    },
});
//# sourceMappingURL=C2FO.js.map